import React, { useState, useRef, useCallback } from 'react';
import useGooglePlaces from '../../hooks/useGooglePlaces';

export default function BookingContactForm({ bookingData, initialData, equipmentData, onSubmit, onBack }) {
     const needsAddress = equipmentData?.delivery?.method === 'delivery';

     const [form, setForm] = useState({
          name: initialData?.name || '',
          email: initialData?.email || '',
          phone: initialData?.phone || '',
          address: initialData?.address || '',
          city: initialData?.city || '',
          state: initialData?.state || 'AZ',
          zip: initialData?.zip || '',
          notes: initialData?.notes || '',
     });
     const [errors, setErrors] = useState({});

     const addressInputRef = useRef(null);
     const formRef = useRef(null);

     const handlePlaceSelected = useCallback((place) => {
          setForm((prev) => ({
               ...prev,
               address: place.address,
               city: place.city,
               state: place.state,
               zip: place.zip,
          }));
          setErrors((prev) => ({ ...prev, address: null, city: null, zip: null }));
     }, []);

     useGooglePlaces(addressInputRef, handlePlaceSelected);

     function handleChange(e) {
          setForm({ ...form, [e.target.name]: e.target.value });
          if (errors[e.target.name]) {
               setErrors({ ...errors, [e.target.name]: null });
          }
     }

     function validate() {
          const newErrors = {};
          if (!form.name.trim()) newErrors.name = 'Name is required';
          if (!form.email.trim()) {
               newErrors.email = 'Email is required';
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
               newErrors.email = 'Invalid email address';
          }
          if (!form.phone.trim()) {
               newErrors.phone = 'Phone number is required';
          } else if (!/^[\d\s()+-]{10,}$/.test(form.phone)) {
               newErrors.phone = 'Invalid phone number';
          }
          if (needsAddress) {
               if (!form.address.trim()) newErrors.address = 'Delivery address is required';
               if (!form.city.trim()) newErrors.city = 'City is required';
               if (!form.zip.trim()) newErrors.zip = 'ZIP code is required';
          }
          return newErrors;
     }

     function handleSubmit(e) {
          e.preventDefault();
          const newErrors = validate();
          if (Object.keys(newErrors).length > 0) {
               setErrors(newErrors);
               return;
          }
          onSubmit(form);
     }

     const inputClasses =
          'w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow focus:ring-2 focus:ring-yellow/20 duration-300';

     return (
          <div className='w-full max-w-2xl mx-auto'>
               <h3 className='text-dark uppercase font-bold text-[32px] laptop:text-[40px] leading-tight mb-2'>
                    Contact Information
               </h3>
               <p className='text-darker/70 mb-8'>
                    We'll use this to confirm your booking and send rental details.
               </p>

               <form ref={formRef} onSubmit={handleSubmit} className='bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-4'>
                    <div>
                         <label className='block text-darker/70 text-sm font-semibold mb-1'>
                              Full Name *
                         </label>
                         <input
                              type='text'
                              name='name'
                              value={form.name}
                              onChange={handleChange}
                              placeholder='John Doe'
                              className={inputClasses}
                         />
                         {errors.name && <p className='text-red-600 text-sm mt-1'>{errors.name}</p>}
                    </div>

                    <div>
                         <label className='block text-darker/70 text-sm font-semibold mb-1'>
                              Email Address *
                         </label>
                         <input
                              type='email'
                              name='email'
                              value={form.email}
                              onChange={handleChange}
                              placeholder='john@example.com'
                              className={inputClasses}
                         />
                         {errors.email && <p className='text-red-600 text-sm mt-1'>{errors.email}</p>}
                    </div>

                    <div>
                         <label className='block text-darker/70 text-sm font-semibold mb-1'>
                              Phone Number *
                         </label>
                         <input
                              type='tel'
                              name='phone'
                              value={form.phone}
                              onChange={handleChange}
                              placeholder='(602) 555-1234'
                              className={inputClasses}
                         />
                         {errors.phone && <p className='text-red-600 text-sm mt-1'>{errors.phone}</p>}
                    </div>

                    {needsAddress && (
                         <>
                              <div className='border-t border-gray-200 pt-4'>
                                   <h4 className='text-yellow text-xs uppercase tracking-widest font-bold mb-3'>
                                        Delivery Address
                                   </h4>
                              </div>
                              <div>
                                   <label className='block text-darker/70 text-sm font-semibold mb-1'>
                                        Street Address *
                                   </label>
                                   <input
                                        ref={addressInputRef}
                                        type='text'
                                        name='address'
                                        value={form.address}
                                        onChange={handleChange}
                                        placeholder='Start typing an address...'
                                        className={inputClasses}
                                        autoComplete='off'
                                   />
                                   {errors.address && <p className='text-red-600 text-sm mt-1'>{errors.address}</p>}
                              </div>
                              <div className='grid grid-cols-6 gap-3'>
                                   <div className='col-span-3'>
                                        <label className='block text-darker/70 text-sm font-semibold mb-1'>
                                             City *
                                        </label>
                                        <input
                                             type='text'
                                             name='city'
                                             value={form.city}
                                             onChange={handleChange}
                                             placeholder='Phoenix'
                                             className={inputClasses}
                                        />
                                        {errors.city && <p className='text-red-600 text-sm mt-1'>{errors.city}</p>}
                                   </div>
                                   <div className='col-span-1'>
                                        <label className='block text-darker/70 text-sm font-semibold mb-1'>
                                             State
                                        </label>
                                        <input
                                             type='text'
                                             name='state'
                                             value={form.state}
                                             onChange={handleChange}
                                             maxLength={2}
                                             className={inputClasses}
                                        />
                                   </div>
                                   <div className='col-span-2'>
                                        <label className='block text-darker/70 text-sm font-semibold mb-1'>
                                             ZIP Code *
                                        </label>
                                        <input
                                             type='text'
                                             name='zip'
                                             value={form.zip}
                                             onChange={handleChange}
                                             placeholder='85001'
                                             className={inputClasses}
                                        />
                                        {errors.zip && <p className='text-red-600 text-sm mt-1'>{errors.zip}</p>}
                                   </div>
                              </div>
                         </>
                    )}

                    <div>
                         <label className='block text-darker/70 text-sm font-semibold mb-1'>
                              Project Notes (optional)
                         </label>
                         <textarea
                              name='notes'
                              value={form.notes}
                              onChange={handleChange}
                              placeholder='Square footage, tile type, any special requirements...'
                              rows={3}
                              className={inputClasses}
                         />
                    </div>
               </form>

               <div className='mt-8 flex gap-4 justify-between'>
                    <button
                         type='button'
                         onClick={onBack}
                         className='py-3 px-6 rounded-xl border-2 border-gray-200 text-darker/60 font-semibold hover:border-darker/30 hover:text-darker duration-300'
                    >
                         ← Back
                    </button>
                    <button
                         type='button'
                         onClick={() => formRef.current?.requestSubmit()}
                         className='group relative font-semibold text-base uppercase tracking-wider bg-yellow text-dark pl-10 pr-6 py-4 rounded-xl hover:bg-yellow/90 duration-300 flex items-center whitespace-nowrap'
                    >
                         <span className='text-dark pr-2 absolute left-4 duration-300 group-hover:tracking-[0.15em]'>
                              {'//'}
                         </span>
                         Continue
                    </button>
               </div>
          </div>
     );
}
